import React, { useState } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { GripVertical, MoveRight, CheckSquare, Square, Star } from 'lucide-react';
import { T } from './constants';

export const DndPosition = ({ pos, shelfName, onSelect, isHighlighted, heatmapBg, heatmapBorder, bulkMode, isBulkSelected, onBulkToggle }) => {
  const isOccupied = pos.stock_count > 0;
  const isPreferred = pos.is_preferred;
  const { attributes, listeners, setNodeRef: setDragRef, isDragging, transform } = useDraggable({
    id: `drag_${pos.id}`, data: { posId: pos.id, code: pos.code, position: pos.position, shelfName, stockCount: pos.stock_count, totalQty: pos.total_qty },
    disabled: !isOccupied || bulkMode,
  });
  const { setNodeRef: setDropRef, isOver, active } = useDroppable({
    id: `drop_${pos.id}`, data: { posId: pos.id, code: pos.code, position: pos.position, shelfName },
  });
  const isValidTarget = active && active.id !== `drag_${pos.id}`;
  const showDrop = isOver && isValidTarget;
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0) scale(1.05)`, zIndex: 50 } : undefined;
  const [hover, setHover] = useState(false);

  return (
    <div ref={n => { setDragRef(n); setDropRef(n); }} style={{
        ...style,
        transformStyle: 'preserve-3d',
        transform: style?.transform || (hover && !isDragging ? 'translateY(-3px) rotateX(2deg)' : 'translateY(0)'),
        transition: isDragging ? 'none' : 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        ...(heatmapBg ? { background: heatmapBg, borderColor: heatmapBorder } : {}),
      }}
      {...attributes}
      data-testid={`pos-${pos.position}`}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      className={`group relative rounded-lg border text-center cursor-pointer
        ${isBulkSelected ? 'ring-2 ring-orange-500 bg-orange-500/15 border-orange-500/50' : ''}
        ${isDragging ? 'opacity-20 scale-90 border-dashed border-blue-500/50' : ''}
        ${showDrop ? 'border-blue-500 bg-blue-500/20 scale-110 shadow-[0_0_25px_rgba(59,130,246,0.4)] ring-1 ring-blue-500/50' : ''}
        ${isHighlighted ? 'ring-2 ring-orange-500 shadow-[0_0_20px_rgba(249,115,22,0.5)] animate-pulse' : ''}
        ${!isDragging && !showDrop && !isBulkSelected && isOccupied ? 'border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 hover:border-emerald-500/60 hover:shadow-[0_4px_20px_rgba(34,197,94,0.2)]' : ''}
        ${!isDragging && !showDrop && !isBulkSelected && !isOccupied ? 'border-zinc-700/50 bg-zinc-800/30 hover:border-zinc-600 hover:bg-zinc-800/60 hover:shadow-[0_4px_15px_rgba(0,0,0,0.3)]' : ''}
        ${isValidTarget && !showDrop && !isDragging ? 'border-dashed border-blue-400/30 bg-blue-500/5' : ''}
      `}
      onClick={() => bulkMode ? onBulkToggle?.() : (!isDragging && onSelect())}
    >
      {bulkMode && (
        <div className="absolute top-1 right-1 z-10">
          {isBulkSelected
            ? <CheckSquare className="w-3.5 h-3.5 text-orange-400" />
            : <Square className="w-3.5 h-3.5 text-zinc-600" />}
        </div>
      )}
      {isOccupied && !isDragging && (
        <div className="absolute -top-1.5 -left-1.5 opacity-0 group-hover:opacity-100 transition-all duration-200 z-10" {...listeners}>
          <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center shadow-lg shadow-blue-500/40 hover:scale-125 transition-transform cursor-grab active:cursor-grabbing">
            <GripVertical className="w-3 h-3 text-white" />
          </div>
        </div>
      )}
      {showDrop && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="absolute w-8 h-8 rounded-full bg-blue-500/20 animate-ping" />
          <MoveRight className="w-4 h-4 text-blue-400" />
        </div>
      )}
      <div className="p-2.5">
        {isPreferred && (
          <div className="absolute -top-1.5 -right-1.5 z-10" title="BIN preferat">
            <div className="w-4 h-4 bg-yellow-500 rounded-full flex items-center justify-center shadow-lg shadow-yellow-500/40">
              <Star className="w-2.5 h-2.5 text-yellow-900 fill-current" />
            </div>
          </div>
        )}
        <p className="font-mono text-xs font-semibold" style={{ color: isOccupied ? T.success : T.textDim }}>{pos.position}</p>
        {isOccupied && !isDragging && (
          <div className="mt-1 text-[10px] font-mono font-medium px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 inline-block">{pos.stock_count}</div>
        )}
        {!isOccupied && hover && !isDragging && (
          <div className="mt-1 text-[9px] text-zinc-600">gol</div>
        )}
      </div>
    </div>
  );
};

export const DragGhost = ({ data }) => data ? (
  <div className="px-4 py-3 rounded-xl border border-blue-500/50 shadow-2xl shadow-blue-500/20 pointer-events-none"
    style={{ background: T.paper, transform: 'rotate(-2deg) scale(1.05)' }}>
    <div className="flex items-center gap-2">
      <Package className="w-5 h-5 text-blue-400 animate-pulse" />
      <div>
        <p className="font-mono text-sm font-bold text-white">{data.position}</p>
        <p className="text-[11px] text-blue-400">{data.stockCount} articol(e)</p>
      </div>
    </div>
  </div>
) : null;

import { Package } from 'lucide-react';

export const StatCard = ({ icon: Icon, label, value, color, sub }) => (
  <div className="rounded-xl border p-4 transition-all hover:border-opacity-50"
    style={{ background: T.paper, borderColor: T.border }}
    data-testid={`stat-${label.toLowerCase().replace(/\s/g,'-')}`}>
    <div className="flex items-center justify-between">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <span className="text-2xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope', color }}>{value}</span>
    </div>
    <p className="mt-2 text-xs font-medium" style={{ color: T.textMuted }}>{label}</p>
    {sub && <p className="text-[10px] mt-0.5" style={{ color: T.textDim }}>{sub}</p>}
  </div>
);
